// Web port of app/src/contexts/AuthenticationContext.js — keep in sync with it.
// The mobile provider is platform-agnostic (react + aws-amplify/auth v6, no React
// Native), so its logic is copied near-verbatim. The web needs sign-in, sign-out
// and the forgot-password pair, so the sign-up / email-verification /
// change-password members are intentionally trimmed: those stay on mobile. The
// value shape for the members that remain matches the app; signInWithPasskey and
// signInWithDiscoverablePasskey are web-only.
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {
  confirmSignIn as cognitoConfirmSignIn,
  signOut as cognitoSignOut,
  resetPassword as cognitoResetPassword,
  confirmResetPassword as cognitoConfirmResetPassword,
  fetchAuthSession,
} from 'aws-amplify/auth';

import { reloadPage, watchAccountElsewhere } from './otherTabs.js';
import { SignInAfterResetError } from 'darwin_core/password-reset';
import { getSignInOptions } from 'darwin_core/api/passkeys';
import {
  answerPasskeyChallenge,
  passkeyUsername,
} from '../passkeys/webauthn.js';
import {
  heldTokens,
  startSignIn,
  throwForRefusedStep,
} from 'darwin_core/sign-in';

export const AuthenticationContext = createContext(null);

// Runs one step of a passkey sign-in, and on failure names the step and the
// error in the console before passing the error on. The screen shows one message
// for several causes on purpose, so this is how a failure gets diagnosed. Only
// the error's name and message are logged: there is no password to echo.
async function passkeyStep(step, run) {
  try {
    return await run();
  } catch (error) {
    console.warn(
      `Passkey sign-in failed at ${step}:`,
      error?.name,
      error?.message,
    );
    throw error;
  }
}

// The ID token claims of the session this browser holds, if any. One that
// can't be read counts as none, so the sign-in goes ahead as usual.
async function heldSessionClaims() {
  const tokens = await heldTokens();
  return tokens?.idToken?.payload ?? null;
}

// Consumer hook. Throws outside a provider so a mis-wired tree fails loudly at
// mount rather than silently reading a default and behaving as "signed out".
export function useAuth() {
  const context = useContext(AuthenticationContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthenticationProvider');
  }
  return context;
}

export function AuthenticationProvider({ children }) {
  const [authenticated, setAuthed] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [user, setUser] = useState(null);
  const [currentAuthUser, setCurrentAuthUser] = useState(null);
  // The Cognito groups the account belongs to (the id token's cognito:groups):
  // what decides which development flags the user may switch on (see
  // flags/FlagContext). The app's spelling is kept so the value shape matches.
  const [eligableGroups, setEligableGroups] = useState([]);

  const fetchUser = useCallback(async function fetchUser(forceRefresh = false) {
    let currentUserData;
    try {
      const authSession = await fetchAuthSession({ forceRefresh });
      if (authSession.tokens) {
        const {
          email,
          sub,
          'cognito:groups': groups,
          given_name,
          family_name,
        } = authSession.tokens.idToken.payload;
        currentUserData = { sub, email, given_name, family_name, groups };
        setCurrentAuthUser(currentUserData);
        setAuthed(true);
        setEligableGroups(groups || []);
        setUser({ email, given_name, family_name });
      } else {
        setAuthed(false);
        setUser(null);
        setCurrentAuthUser(null);
        setEligableGroups([]);
      }
    } catch {
      // Never log the error/session here: it can carry token material. A signed-in
      // check that throws simply means "not authenticated".
      setAuthed(false);
    } finally {
      setLoaded(true);
    }

    return currentUserData;
  }, []);

  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // When another tab signs in, signs out, or signs in to another account (see
  // otherTabs.js), follow it by reading the shared session again. An account
  // appearing or going is an ordinary sign-in or sign-out here (StoreBootstrap
  // seeds or resets the store), so what this tab has typed stays put. Only a
  // switch from one account straight to another reloads, before this tab shows
  // anything of it, because its store still holds the first account's data.
  // The account this tab is showing: none while signed out, even when a check
  // that failed on the network has left currentAuthUser as it was.
  const shownAccount = useRef(null);
  useEffect(() => {
    shownAccount.current = authenticated
      ? (currentAuthUser?.sub ?? null)
      : null;
  }, [authenticated, currentAuthUser]);
  useEffect(
    () =>
      watchAccountElsewhere(async () => {
        let storedAccount = null;
        try {
          const { tokens } = await fetchAuthSession();
          storedAccount = tokens?.idToken?.payload?.sub ?? null;
        } catch {
          // Left to fetchUser, which treats a failed check as signed out.
        }
        if (
          shownAccount.current &&
          storedAccount &&
          shownAccount.current !== storedAccount
        ) {
          reloadPage();
          return;
        }
        fetchUser();
      }),
    [fetchUser],
  );

  // `overSession` is whether to sign in over a session another tab started:
  // yes when someone types their password on Login (as signInWithPasskey does
  // for a passkey), never for a reset (see forgotPasswordSubmit).
  const signInAs = useCallback(
    async (emailAddress, password, { overSession }) => {
      const signInResult = await startSignIn(
        { username: emailAddress.toLowerCase(), password },
        { overSession },
      );

      const step = signInResult.nextStep?.signInStep;
      if (step && step !== 'DONE') {
        throwForRefusedStep(signInResult.nextStep);
        throw new Error('UnknownNextStep');
      }

      return fetchUser();
    },
    [fetchUser],
  );

  const signIn = useCallback(
    (emailAddress, password) =>
      signInAs(emailAddress, password, { overSession: true }),
    [signInAs],
  );

  // The Cognito half of a passkey sign-in, through custom auth: starts one for
  // `username`, has `answer` sign the WebAuthn challenge Cognito issues (given
  // its request options, as the JSON string Cognito sends), and has Cognito
  // check the answer.
  //
  // A sign-in that fails part-way leaves Amplify's in-progress state (the
  // username and Cognito session) in sessionStorage until it expires after
  // three minutes. Amplify has no call to clear it, and the next sign-in
  // replaces it, so it is left there.
  const passkeySignIn = useCallback(
    async (username, answer) => {
      const challenge = await passkeyStep('start (Cognito)', () =>
        startSignIn({
          username,
          options: { authFlowType: 'CUSTOM_WITHOUT_SRP' },
        }),
      );
      throwForRefusedStep(challenge.nextStep);

      if (
        challenge.nextStep?.signInStep !==
        'CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE'
      ) {
        console.warn(
          'Passkey sign-in got an unexpected step from Cognito:',
          challenge.nextStep?.signInStep,
        );
        throw new Error('UnknownNextStep');
      }

      const signed = await answer(challenge.nextStep.additionalInfo?.options);
      const result = await passkeyStep('passkey check (Cognito)', () =>
        cognitoConfirmSignIn({
          challengeResponse: JSON.stringify(signed),
        }),
      );

      // Cognito offers another attempt rather than failing outright when the
      // passkey isn't one of this account's (the browser may offer any Halo
      // passkey it holds, including another account's).
      if (result.nextStep?.signInStep !== 'DONE') {
        console.warn(
          'Passkey sign-in: Cognito did not accept the passkey:',
          result.nextStep?.signInStep,
        );
        throw new Error('PasskeyNotAccepted');
      }

      return fetchUser();
    },
    [fetchUser],
  );

  // Signs in with a passkey for the email the user typed: Cognito issues a
  // WebAuthn challenge, the browser signs it with a passkey the user picks
  // (after their fingerprint, face or device PIN), and Cognito checks it.
  const signInWithPasskey = useCallback(
    async (emailAddress) => {
      // Another tab may have signed this account in since this one loaded.
      // Pick that session up rather than let startSignIn end it before the
      // passkey is checked: cancelling the prompt then would sign the other
      // tab out for nothing. Another account's session is still ended, as a
      // password sign-in ends it, since signing in has to replace it.
      const held = await heldSessionClaims();
      if (held?.email?.toLowerCase() === emailAddress.toLowerCase()) {
        const session = await fetchUser();
        if (session) {
          return session;
        }
      }

      return passkeySignIn(emailAddress.toLowerCase(), async (options) => {
        if (!options) {
          console.warn(
            'Passkey sign-in got a challenge without options from Cognito',
          );
          throw new Error('UnknownNextStep');
        }
        // Its own step, so a bad challenge from the server isn't logged as
        // the browser failing to sign it.
        const requestOptions = await passkeyStep(
          'challenge options (Cognito)',
          async () => JSON.parse(options),
        );
        return passkeyStep('passkey prompt (browser)', () =>
          answerPasskeyChallenge(requestOptions),
        );
      });
    },
    [fetchUser, passkeySignIn],
  );

  // Signs in with a passkey without an email: the API issues a challenge
  // before the account is known, the browser signs it with whichever Halo
  // passkey the user picks, and the passkey's user handle names the account.
  // Cognito then starts a sign-in for that account, and takes the answer to
  // the API's challenge in place of its own, which is ignored.
  //
  // The user handle isn't covered by the passkey's signature, so anyone can
  // set it. The sign-in therefore always goes all the way through Cognito,
  // never picking up a session this browser already holds on its say-so:
  // startSignIn ends such a session and signs in again. The browser asked for
  // the passkey before this started, so nothing is lost if the user closes
  // its prompt.
  //
  // `autofilled` is an answer the browser has already given, when the user
  // picked a passkey from the email field's autofill (see
  // offerPasskeyAutofill). Without one, the browser's prompt asks for one.
  const signInWithDiscoverablePasskey = useCallback(
    async (autofilled) => {
      let answer = autofilled;
      if (!answer) {
        const options = await passkeyStep(
          'sign-in options (Halo API)',
          getSignInOptions,
        );
        answer = await passkeyStep('passkey prompt (browser)', () =>
          answerPasskeyChallenge(options),
        );
      }
      const username = await passkeyStep('user handle (browser)', async () =>
        passkeyUsername(answer),
      );
      return passkeySignIn(username, async () => answer);
    },
    [passkeySignIn],
  );

  const signOut = useCallback(async () => {
    await cognitoSignOut();
    setAuthed(false);
    setUser(null);
    setCurrentAuthUser(null);
    setEligableGroups([]);
  }, []);

  // Re-check whether the Cognito session still exists, and sign out locally if it
  // has been revoked. Called when the live socket dies (see SessionGate), which is
  // the main signal a read-only client gets that a session may have ended.
  //
  // forceRefresh forces the refresh-token round-trip to Cognito: a NON-forced
  // fetch returns the locally-cached id/access tokens without a network call while
  // they are still unexpired (~60 min), so it would NOT notice a server-side
  // revocation until the tokens expired. With forceRefresh a revoked refresh token
  // makes amplify clear its stored tokens and return an empty session (no tokens)
  // -> we sign out immediately. A transient NETWORK failure still throws and is
  // caught below, so this does not add a false-sign-out risk on a flaky link.
  const revalidateSession = useCallback(async () => {
    try {
      const session = await fetchAuthSession({ forceRefresh: true });
      if (!session?.tokens) {
        // Definitively no session (revoked, nothing left to refresh) -> clear
        // local auth so the app returns to sign-in. Tokens are already gone from
        // the auth-storage seam at this point, so a cognitoSignOut is unnecessary.
        setAuthed(false);
        setUser(null);
        setCurrentAuthUser(null);
        setEligableGroups([]);
        return false;
      }
      return true;
    } catch {
      // Ambiguous (usually a transient network failure): do NOT sign the user out
      // on this alone — a revoked session returns empty tokens above, it does not
      // throw here.
      return true;
    }
  }, []);

  const forgotPassword = useCallback(async (emailAddress) => {
    // clientMetadata is what the mobile app sends. The custom-message Lambda that
    // builds the reset email and its link to /password-code (api.git,
    // src/cognito/custom-message.ts) doesn't read it today, so a web-specific
    // ResetOrigin would change nothing until that Lambda did.
    await cognitoResetPassword({
      username: emailAddress.toLowerCase(),
      clientMetadata: { ResetOrigin: 'HaloApp', AccountType: 'Halo' },
    });
  }, []);

  // Sets the new password, then resolves to what became of this browser's
  // session: { session } when it is signed in to the reset account, or
  // { keptAccount } (that account's email) when another account stays signed in.
  //
  // Whose session the browser holds is read from amplify's stored tokens, not
  // from `authenticated`, which is false while tokens remain if the check on
  // load failed on the network. Then:
  //   - another account stays signed in. A link, perhaps one crafted to set a
  //     password on someone else's account, mustn't switch the browser to the
  //     account it names;
  //   - this account keeps its session, as there is nothing to switch;
  //   - with none, it signs in with the new password, as the app's does.
  //
  // Another tab can sign in between that check and the sign-in, so the sign-in
  // never goes over a session: if one has appeared, it is treated as found by
  // the check.
  //
  // If the stored session can't be read, or signing in fails, the password is
  // still set, so that is thrown as a SignInAfterResetError: the screen can
  // say the reset worked instead of inviting a retry with a code now used up.
  const forgotPasswordSubmit = useCallback(
    async (emailAddress, code, password) => {
      const username = emailAddress.toLowerCase();
      await cognitoConfirmResetPassword({
        username,
        confirmationCode: code,
        newPassword: password,
      });

      // What becomes of the session the browser holds, or null if none.
      const existingSession = async () => {
        const { tokens } = await fetchAuthSession();
        if (!tokens) {
          return null;
        }
        const storedEmail = tokens.idToken?.payload?.email;
        // Brings `authenticated` up to date with the session either way.
        const session = await fetchUser();
        if (storedEmail?.toLowerCase() !== username) {
          return { keptAccount: storedEmail };
        }
        return session ? { session } : {};
      };

      let outcome;
      try {
        outcome = await existingSession();
        if (!outcome) {
          try {
            const session = await signInAs(username, password, {
              overSession: false,
            });
            outcome = session ? { session } : {};
          } catch (ex) {
            if (ex?.name !== 'UserAlreadyAuthenticatedException') {
              throw ex;
            }
            outcome = (await existingSession()) ?? {};
          }
        }
      } catch (ex) {
        throw new SignInAfterResetError(ex);
      }
      if (!outcome.session && !('keptAccount' in outcome)) {
        throw new SignInAfterResetError();
      }
      return outcome;
    },
    [fetchUser, signInAs],
  );

  const value = useMemo(
    () => ({
      authenticated,
      loaded,
      user: user || {},
      currentAuthUser,
      eligableGroups,
      signIn,
      signInWithPasskey,
      signInWithDiscoverablePasskey,
      signOut,
      forgotPassword,
      forgotPasswordSubmit,
      revalidateSession,
    }),
    [
      authenticated,
      loaded,
      user,
      currentAuthUser,
      eligableGroups,
      signIn,
      signInWithPasskey,
      signInWithDiscoverablePasskey,
      signOut,
      forgotPassword,
      forgotPasswordSubmit,
      revalidateSession,
    ],
  );

  return (
    <AuthenticationContext.Provider value={value}>
      {children}
    </AuthenticationContext.Provider>
  );
}
